import React, { useCallback, useEffect, useRef } from 'react';

// iOS only synthesizes mouse/click events after a tap has finished, and drops them entirely when a
// mouseover/mouseenter/mouseleave handler mutated the DOM during that same tap. Closing on
// pointerdown (dispatched straight from the touch) keeps the outside-click behaviour reliable there.
const OUTSIDE_EVENTS =
  typeof window !== 'undefined' && 'PointerEvent' in window
    ? ['pointerdown']
    : ['mousedown', 'touchstart'];

function useComponentVisible(
  setShow: React.Dispatch<React.SetStateAction<number | boolean>>,
  value?: number
) {
  const childRef = useRef(null);
  const parentRef = useRef(null);

  const handleClickOutside = useCallback((event: any) => {
    if (!childRef.current) return;
    const target = event.target as Node;
    if (childRef.current.contains(target)) return;
    if (parentRef.current?.contains(target)) return;

    setShow(value || false);
  }, [setShow, value]);

  useEffect(() => {
    OUTSIDE_EVENTS.forEach(name => document.addEventListener(name, handleClickOutside, true));
    return () => {
      OUTSIDE_EVENTS.forEach(name => document.removeEventListener(name, handleClickOutside, true));
    };
  }, [handleClickOutside]);

  return { parentRef, ref: childRef };
}

export default useComponentVisible;
